import React from "react";

import { reduxForm, SubmissionError, change } from "redux-form";
import fetchJsonp from "fetch-jsonp";
import qs from "query-string";
import { withRouter } from "react-router-dom";
import withHooks, { useEffect, useState } from "react-with-hooks";

import removeHash from "src/utils/location";
import WithUserForm from "../../Register/WithUserForm";
import ApplicationLayout, { applicationStyles as styles } from "../ApplicationLayout";
import CheckingInstagram from "../CheckingInstagram";

const REQUIRED_PHOTOS = 6;
const PHOTOS_PER_PAGE = 12;

const Tick = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#B08D57" strokeWidth="1.8" aria-hidden="true">
    <path d="M3 8.5 6.5 12 13 4.5" />
  </svg>
);

const PhotoGrid = ({ images, selected, onToggle, locked }) => (
  <div className={styles.photoGrid}>
    {images.map((image) => {
      const position = selected.findIndex((one) => one.id === image.id);
      const chosen = position >= 0;
      return (
        <button
          key={image.id}
          type="button"
          aria-pressed={chosen}
          aria-label={chosen ? `Photo chosen, ${position + 1} of ${REQUIRED_PHOTOS}` : "Choose this photo"}
          disabled={locked}
          className={`${styles.tile} ${chosen ? styles.tileSelected : ""} ${locked ? styles.tileLocked : ""}`}
          onClick={() => onToggle(image)}
        >
          <img className={styles.tileImage} src={image.media_url} alt="" />
          {chosen && <span className={styles.tileBadge}>{position + 1}</span>}
        </button>
      );
    })}
  </div>
);

const Form = withHooks(({
  form: { handleSubmit },
  imageData,
  imagesSelected,
  selectImage,
  handleNextPage,
  loadingMore,
  connectedInstagramHandle,
}) => {
  const images = imageData?.images || [];
  const tooFew = images.length < REQUIRED_PHOTOS;
  const chosen = imagesSelected.length;
  const ready = chosen === REQUIRED_PHOTOS;
  const [full, setFull] = useState(false);
  const [visibleCount, setVisibleCount] = useState(PHOTOS_PER_PAGE);

  const visible = images.slice(0, visibleCount);
  // More to reveal from what is already loaded, or another page to ask Instagram for.
  const hasMore = visibleCount < images.length || !!imageData?.paging?.next;

  const showMore = async () => {
    if (visibleCount >= images.length) await handleNextPage();
    setVisibleCount((count) => count + PHOTOS_PER_PAGE);
  };

  const toggle = (image) => {
    const alreadyChosen = imagesSelected.some((one) => one.id === image.id);
    if (!alreadyChosen && chosen >= REQUIRED_PHOTOS) {
      setFull(true);
      return;
    }
    setFull(false);
    selectImage(image);
  };

  const footer = (
    <>
      {!tooFew && (
        <>
          <div role="status" className={`${styles.chosenCount} ${ready ? styles.chosenCountFull : ""}`}>
            {chosen} of {REQUIRED_PHOTOS} chosen
          </div>
          {full && (
            <div className={styles.chosenHint}>
              You've chosen {REQUIRED_PHOTOS}. Tap a chosen photo to swap it out.
            </div>
          )}
        </>
      )}
      <div className={styles.actions}>
        {tooFew ? (
          <button type="button" className={styles.cta} onClick={() => window.location.reload()}>
            Check again
          </button>
        ) : (
          <button type="submit" className={styles.cta} disabled={!ready}>Continue</button>
        )}
        <p className={styles.footerNote}>
          Having trouble? <a href="https://wa.me/443330166335">Contact us</a>
        </p>
      </div>
    </>
  );

  return (
    <ApplicationLayout
      as="form"
      onSubmit={handleSubmit}
      step={6}
      aboveTitle={
        <div className={styles.connected}>
          <Tick />
          <span>
            <span className={styles.connectedHandle}>@{connectedInstagramHandle}</span> is connected
          </span>
        </div>
      }
      title={tooFew ? `You need ${REQUIRED_PHOTOS} photos` : `Choose ${REQUIRED_PHOTOS} photos`}
      intro={
        tooFew
          ? `Your Instagram has ${images.length} ${images.length === 1 ? "photo" : "photos"}, and you need at least ${REQUIRED_PHOTOS} to apply. Post a few more, then come back and check again. Your answers so far are saved.`
          : "Pick the photos that best show you and your work. They'll be the first thing we see."
      }
      footer={footer}
    >
      <PhotoGrid images={visible} selected={imagesSelected} onToggle={toggle} locked={tooFew} />

      {!tooFew && hasMore && (
        <button type="button" className={styles.secondaryButton} onClick={showMore} disabled={loadingMore}>
          {loadingMore ? "Loading…" : "Show more photos"}
        </button>
      )}
    </ApplicationLayout>
  );
});

let StepFive = ({ history, dispatch }) => {
  const [instagramToken, setInstagramToken] = useState();
  const [imagesSelected, setImagesSelected] = useState([]);
  const [imageData, setImageData] = useState({});
  const [loadingMore, setLoadingMore] = useState(false);
  const [connectedInstagramHandle, setConnectedInstagramHandle] = useState(null);

  useEffect(() => {
    if (instagramToken) {
      getImages();
    }
  }, [instagramToken]);

  const handleSelectImage = (image) => {
    const index = imagesSelected.findIndex((img) => img.id === image.id);
    const newImageList = [...imagesSelected];
    if (newImageList.length <= REQUIRED_PHOTOS && index > -1) {
      newImageList.splice(index, 1);
    } else if (newImageList.length < REQUIRED_PHOTOS) {
      newImageList.push(image);
    }
    setImagesSelected(newImageList);
  };

  const handleImages = async (body) => {
    let images = [];
    const media = body.data.filter(
      (m) => m.media_type === "IMAGE" || m.media_type === "CAROUSEL_ALBUM"
    );

    if (media.length > 0) {
      for (const post of media) {
        if (post.children) {
          const children = post.children.data.filter(
            (child) => child.media_type === "IMAGE" || child.media_type === "CAROUSEL_ALBUM"
          );
          if (children.length > 0) {
            const rootPermalink = children[0].permalink;
            children.forEach((c, i) => {
              c.carouselRootPermalink = rootPermalink;
              c.carouselChildIndex = i + 1;
            });
            images = images.concat(children);
          }
        } else {
          images.push(post);
        }
      }
      setConnectedInstagramHandle(media[0].username);
    }

    setImageData((prev) => ({
      images: [...(prev?.images || []), ...images],
      paging: body.paging,
    }));
  };

  const handleNextPage = async () => {
    if (!imageData?.paging?.next) return;
    setLoadingMore(true);
    try {
      const res = await fetchJsonp(imageData.paging.next).then((res) => res.json());
      await handleImages(res);
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingMore(false);
    }
  };

  const getImages = async () => {
    removeHash();
    const query = qs.stringify({ access_token: instagramToken });
    const res = await fetchJsonp(
      `https://graph.instagram.com/v21.0/me/media/?fields=id,media_type,media_url,permalink,children{id,media_type,media_url,permalink},username&${query}`
    )
      .then((res) => res.json())
      .catch((error) => {
        // Failed to fetch instagram account images, invalid token
        console.log(error);
        history.push("/apply/instagram");
      });
    if (res.error) {
      // Failed to fetch instagram account images, invalid token
      console.log(res.error);
      history.push("/apply/instagram");
    }
    await handleImages(res);
  };

  const onSubmit = async (values, _, formProps) => {
    if (imagesSelected.length < REQUIRED_PHOTOS) {
      throw new SubmissionError({
        images: `Please select at least ${REQUIRED_PHOTOS} photos.`,
      });
    }

    const formattedValues = WithUserForm.formatUserPhotosInput({
      ...values,
      images: imagesSelected,
      connectedInstagramHandle,
    });

    dispatch(change("apply", "images", imagesSelected));
    dispatch(change("apply", "connectedInstagramHandle", connectedInstagramHandle));

    await formProps.updateUserPhotos({ variables: { input: formattedValues } });
    formProps.machine.service.send({ type: "NEXT" });
  };

  return (
    <WithUserForm
      onSubmit={onSubmit}
      customLoading={() => <CheckingInstagram onRetry={() => history.push("/apply/instagram")} />}
    >
      {(formProps) => {
        useEffect(() => {
          const dbInstagramAccessToken = formProps.userQuery?.data?.currentUser?.instagramToken;
          if (!dbInstagramAccessToken) {
            history.push("/apply/instagram");
          }
          setInstagramToken(dbInstagramAccessToken);
        }, []);

        if (!connectedInstagramHandle) {
          return <CheckingInstagram onRetry={() => history.push("/apply/instagram")} />;
        }

        return (
          <Form
            {...formProps}
            imageData={imageData}
            imagesSelected={imagesSelected}
            selectImage={handleSelectImage}
            handleNextPage={handleNextPage}
            loadingMore={loadingMore}
            connectedInstagramHandle={connectedInstagramHandle}
          />
        );
      }}
    </WithUserForm>
  );
};

StepFive = reduxForm({
  form: "apply",
  enableReinitialize: true,
  destroyOnUnmount: false,
})(StepFive);

export default withRouter(StepFive);